import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Download, Sparkles, Workflow, Microscope, X } from "lucide-react";
import { api, API_BASE, formatApiErrorDetail, getStoredToken } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const FORMATS = [
  { key: "markdown", label: "Markdown", ext: "md" },
  { key: "html",     label: "HTML",     ext: "html" },
  { key: "pdf",      label: "PDF",      ext: "pdf" },
  { key: "docx",     label: "Word",     ext: "docx" },
  { key: "pptx",     label: "PPT",      ext: "pptx" },
];

export default function ProductionPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [catalog, setCatalog] = useState([]);
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [kind, setKind] = useState(searchParams.get("kind") || "medical_report");
  const [memberId, setMemberId] = useState("");
  const [title, setTitle] = useState("");
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");
  const [inspectorRefId, setInspectorRefId] = useState(null);
  const [inspectorItem, setInspectorItem] = useState(null);
  const [inspectorBusy, setInspectorBusy] = useState(false);

  useEffect(() => {
    api.get(`/production/catalog?language=${language}`).then((r) => setCatalog(r.data || []));
    reloadDocs();
    if (user?.role !== "member") {
      api.get("/mi/users?role=member").then((r) => {
        const list = r.data || [];
        setMembers(list);
        if (list[0]) setMemberId(list[0].id);
      }).catch(() => {});
    } else {
      setMemberId(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, user?.id]);

  async function reloadDocs() {
    try {
      const r = await api.get("/production/documents");
      setDocs(r.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function generate() {
    setBusy(true);
    setError("");
    setActive(null);
    try {
      const { data } = await api.post("/production/document", {
        kind,
        member_id: user?.role === "member" ? undefined : memberId,
        language,
        title: title || undefined,
        focus: focus || undefined,
      });
      setActive(data);
      reloadDocs();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function openDoc(id) {
    try {
      const { data } = await api.get(`/production/document/${id}`);
      setActive(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function openInspector(refId) {
    setInspectorRefId(refId);
    setInspectorItem(null);
    setInspectorBusy(true);
    try {
      const { data } = await api.get(`/knowledge/items/${refId}`);
      setInspectorItem(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setInspectorBusy(false);
    }
  }

  function downloadUrl(id, fmt) {
    const token = getStoredToken();
    const sep = `${API_BASE}/production/document/${id}/download?fmt=${fmt}`;
    return token ? `${sep}&token=${encodeURIComponent(token)}` : sep;
  }

  async function downloadFile(id, fmt) {
    try {
      const res = await api.get(`/production/document/${id}/download?fmt=${fmt}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (active?.title || "document").replace(/[^A-Za-z0-9._-]+/g, "_");
      a.download = `${safe}.${FORMATS.find((f) => f.key === fmt)?.ext || fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="production-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.production.eyebrow}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{t.production.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{t.production.sub}</p>
      </header>

      {error && (
        <div className="mios-card" data-testid="production-error">
          <div className="mios-card-body text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[360px,1fr]">
        {/* Generator */}
        <aside className="mios-card" data-testid="production-generator">
          <div className="mios-card-header">
            <div className="mios-card-title flex items-center gap-2">
              <Workflow className="h-4 w-4 text-slate-400" />
              {t.production.generator}
            </div>
            <Sparkles className="h-4 w-4 text-slate-300" />
          </div>
          <div className="mios-card-body space-y-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                {t.production.kind}
              </div>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                data-testid="production-kind-select"
                className="mios-input mt-1"
              >
                {catalog.map((c) => (
                  <option key={c.kind} value={c.kind}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            {user?.role !== "member" && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  {t.production.member}
                </div>
                <select
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  data-testid="production-member-select"
                  className="mios-input mt-1"
                >
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} · {m.email}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                {t.production.titleField}
              </div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t.production.titlePlaceholder}
                data-testid="production-title-input"
                className="mios-input mt-1"
              />
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                {t.production.focus}
              </div>
              <input
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                placeholder={t.production.focusPlaceholder}
                data-testid="production-focus-input"
                className="mios-input mt-1"
              />
            </div>

            <button
              type="button"
              onClick={generate}
              disabled={busy}
              data-testid="production-generate-btn"
              className="mios-btn mios-btn-primary w-full"
            >
              <FileText className="h-4 w-4" />
              {busy ? t.production.generating : t.production.generate}
            </button>
            {busy && (
              <p className="text-[11px] text-slate-500" data-testid="production-busy-hint">
                {t.production.busyHint}
              </p>
            )}
          </div>
        </aside>

        {/* Active doc */}
        <section className="space-y-4">
          {busy ? (
            <div className="mios-card" data-testid="production-busy-card">
              <div className="mios-card-body flex items-center gap-3 text-sm text-slate-600">
                <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-slate-900" />
                {t.production.generating} — {t.production.busyHint}
              </div>
            </div>
          ) : active ? (
            <div className="mios-card" data-testid="production-active-doc">
              <div className="mios-card-header">
                <div>
                  <div className="mios-card-sub">{t.production.activeDocument}</div>
                  <div className="mt-1 text-xl font-semibold text-slate-900">{active.title}</div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                    {active.kind} · {active.language?.toUpperCase()} ·{" "}
                    {new Date(active.created_at).toLocaleString(language)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {FORMATS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => downloadFile(active.id, f.key)}
                      data-testid={`production-download-${f.key}`}
                      className="mios-btn mios-btn-ghost text-xs"
                      title={f.label}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mios-card-body">
                <div className="rounded-md border border-slate-200 bg-slate-50/40 p-4">
                  <pre
                    data-testid="production-markdown-preview"
                    className="max-h-[520px] overflow-auto whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-800"
                  >
                    {active.markdown || ""}
                  </pre>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  <span className="mios-pill mios-pill-normal mono">{active.provider}</span>
                  <span className="mono">{active.model}</span>
                </div>
                {(active.knowledge_refs || []).length > 0 && (
                  <div className="mt-4 border-t border-slate-200 pt-3" data-testid="evidence-inspector-bar">
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                        {t.production.evidenceTitle}
                      </div>
                      <span className="mios-pill mios-pill-low mono">{active.knowledge_refs.length}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {active.knowledge_refs.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => openInspector(r.id)}
                          data-testid={`evidence-chip-${r.id}`}
                          className="rounded-md border border-slate-200 px-2.5 py-1.5 text-left text-[11px] transition-colors hover:border-slate-400 hover:bg-slate-50"
                          title={r.title}
                        >
                          <span className="mono uppercase text-slate-500">{r.evidence_level}</span>
                          <span className="ml-1.5 text-slate-700">{(r.title || "").slice(0, 40)}{r.title && r.title.length > 40 ? "…" : ""}</span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-[10px] text-slate-500">{t.production.evidenceHint}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mios-card">
              <div className="mios-card-body text-sm text-slate-500">{t.production.empty}</div>
            </div>
          )}

          <div className="mios-card" data-testid="production-history-card">
            <div className="mios-card-header">
              <div className="mios-card-title">{t.production.history}</div>
              <span className="text-[11px] text-slate-400">{docs.length}</span>
            </div>
            <div className="mios-card-body">
              <ul className="space-y-2">
                {docs.length === 0 && <li className="text-sm text-slate-400">{t.production.noHistory}</li>}
                {docs.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => openDoc(d.id)}
                      data-testid={`production-history-row-${d.id}`}
                      className="flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-left text-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
                    >
                      <div>
                        <div className="font-medium text-slate-900">{d.title}</div>
                        <div className="text-[11px] text-slate-500">
                          {d.kind} · {new Date(d.created_at).toLocaleString(language)}
                        </div>
                      </div>
                      <span className="mios-pill mios-pill-normal mono">{d.format}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* Evidence Inspector — slide-in drawer */}
      {inspectorRefId && (
        <div
          className="fixed inset-0 z-30 flex justify-end bg-slate-900/30 backdrop-blur-sm"
          onClick={() => setInspectorRefId(null)}
          data-testid="evidence-inspector"
        >
          <aside
            className="h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1.5">
                  <Microscope className="h-3.5 w-3.5" />
                  {t.production.evidenceTitle}
                </div>
                <div className="mt-0.5 text-base font-semibold text-slate-900">
                  {inspectorItem
                    ? (language === "tr" ? inspectorItem.title_tr : inspectorItem.title_en) || inspectorItem.title_en
                    : t.common.loading}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setInspectorRefId(null)}
                data-testid="evidence-inspector-close"
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              {inspectorBusy && <div className="text-sm text-slate-500">{t.common.loading}</div>}
              {!inspectorBusy && inspectorItem && (
                <div className="space-y-3 text-sm">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="mios-pill mios-pill-normal mono">{inspectorItem.evidence_level}</span>
                    <span className="mios-pill mios-pill-low mono">{inspectorItem.tier}</span>
                    <span className="mios-pill mios-pill-low mono">{inspectorItem.publication_date}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
                    <Meta label={t.production.evidenceMeta.region} value={inspectorItem.region} />
                    <Meta label={t.production.evidenceMeta.domain} value={inspectorItem.domain} />
                    <Meta label={t.production.evidenceMeta.source_type} value={inspectorItem.source_type} />
                    <Meta label={t.production.evidenceMeta.reliability} value={`${Math.round((inspectorItem.reliability || 0) * 100)}%`} />
                    <Meta label={t.production.evidenceMeta.version} value={inspectorItem.version} />
                    <Meta label={t.production.evidenceMeta.layer} value={inspectorItem.layer} />
                  </div>
                  {(language === "tr" ? inspectorItem.abstract_tr : inspectorItem.abstract_en) && (
                    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
                      {language === "tr" ? inspectorItem.abstract_tr : inspectorItem.abstract_en}
                    </div>
                  )}
                  {inspectorItem.source_url && (
                    <a
                      href={inspectorItem.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-600 underline"
                    >
                      {inspectorItem.source_url}
                    </a>
                  )}
                  <p className="text-[11px] text-slate-500">{t.production.evidenceNote}</p>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="uppercase tracking-[0.1em] text-slate-500">{label}</div>
      <div className="mono mt-0.5 text-slate-800">{value || "—"}</div>
    </div>
  );
}
